import { Button, Group, Text } from '@mantine/core'
import { useNavigate } from 'react-router-dom'
import { SectionAction, SectionCard, StatusBadge, EmptyState, Skeleton } from '@lighthouse/design'
import { IconAlertCircle, IconCircleCheck, IconMinus } from '@tabler/icons-react'
import { useEtlHalts } from '../../hooks/useEtlHalts'
import { useEtlJobCatalog } from '../../hooks/useEtlJobs'
import { useLiveOperations } from '../../hooks/useLiveOperations'
import { useCombSummary } from '../../hooks/useDataComb'
import { agoLabel } from '../../utils/format'
import {
  ALL_CLEAR_TEXT, combCondition, haltCondition, isAllClear, staleCondition,
} from './overviewFormat'
import type { AttentionCondition } from './overviewFormat'

/**
 * One measured condition, in whichever of its five states it is in.
 *
 * Every state looks different on purpose: a request in flight is a skeleton, a
 * failed one says so in words, an unmeasured answer is a dash, and only a
 * MEASURED zero gets the check mark. Only `attention` is coloured and carries an
 * action button; the rest still link, quietly, to the surface that owns them.
 */
export function ConditionCard({ condition }: { condition: AttentionCondition }) {
  const navigate = useNavigate()

  if (condition.state === 'loading') {
    return (
      <div className="lh-attention-card" data-condition={condition.key} data-state="loading" aria-busy="true">
        <span className="lh-attention-desc">{condition.label}</span>
        <Skeleton height={14} width="70%" />
        <Skeleton height={10} width="90%" />
      </div>
    )
  }

  if (condition.state !== 'attention') {
    const icon = condition.state === 'clear'
      ? <span style={{ color: 'var(--status-good)' }}><IconCircleCheck size={15} /></span>
      : condition.state === 'error'
        ? <span style={{ color: 'var(--status-critical-fg)' }}><IconAlertCircle size={15} /></span>
        : <span style={{ color: 'var(--color-text-disabled)' }}><IconMinus size={15} /></span>
    return (
      <div className="lh-attention-card" data-condition={condition.key} data-state={condition.state}>
        <div className="lh-attention-head">
          <span className="lh-attention-desc">{condition.label}</span>
          {condition.occurredAt && <span className="lh-attention-time">{agoLabel(condition.occurredAt)}</span>}
        </div>
        <span className="lh-attention-title" style={{ display: 'flex', gap: 6, alignItems: 'flex-start', fontWeight: 500 }}>
          {icon}
          <span>{condition.title}</span>
        </span>
        {condition.detail && <span className="lh-attention-desc">{condition.detail}</span>}
        <div style={{ marginTop: 2 }}>
          <Button size="compact-xs" variant="subtle" onClick={() => navigate(condition.target)}>
            {condition.actionLabel}
          </Button>
        </div>
      </div>
    )
  }

  const critical = condition.tone === 'critical'
  return (
    <div
      className="lh-attention-card"
      data-condition={condition.key}
      data-state="attention"
      // The card's border colour hangs off data-severity (shell.css). These are
      // measured conditions, not signals, so the badge is a status — the colour
      // just borrows the severity rail's two strongest tones.
      data-severity={critical ? 'critical' : 'high'}
    >
      <div className="lh-attention-head">
        <StatusBadge status={critical ? 'critical' : 'warning'} label={condition.label} />
        {condition.occurredAt && <span className="lh-attention-time">{agoLabel(condition.occurredAt)}</span>}
      </div>
      <span className="lh-attention-title">{condition.title}</span>
      {condition.detail && <span className="lh-attention-desc">{condition.detail}</span>}
      {condition.consequence && <span className="lh-attention-desc">{condition.consequence}</span>}
      <div style={{ marginTop: 2 }}>
        <Button
          size="compact-xs"
          variant={critical ? 'filled' : 'default'}
          color={critical ? 'red' : undefined}
          onClick={() => navigate(condition.target)}
        >
          {condition.actionLabel}
        </Button>
      </div>
    </div>
  )
}

/**
 * Critical Attention — the "what needs a human" answer, from three MEASURED
 * conditions, each with its own loading and error state:
 *
 * - Active ETL halts (`api/etl/halts`, blocking only). A halt refuses every
 *   request but the categories the server exempts until an administrator clears
 *   it — the sentence is built from the served exempt list — and the Overview
 *   never used to show one.
 * - Stale ETL runs (Live Operations' uncapped counts, by the worker's own rule).
 *   Nothing closes a dead worker's run, so these accumulate until someone looks.
 * - Outstanding critical Data Comb findings on the latest completed pass.
 *
 * These are read DIRECTLY from the surfaces that own them, not from the signal
 * store — so the card stays right when the detector is down, which is exactly when
 * it matters. The same three conditions are also signal rules (etl-halt-active,
 * etl-run-stale, comb-critical-finding), and the detector reads the same tables
 * every 60 s, so this panel may LEAD the store by up to one detector pass: a halt
 * can show here a minute before the Signal Inbox and the Critical tile count it.
 * The card links to the Inbox rather than repeating it; the fixture that once
 * filled this space included a critical owned by a real person.
 *
 * The all-clear is QUALIFIED and appears only when all three are measured clear:
 * a panel that says "Nothing needs attention" while it measures three things, one
 * of which may not have loaded, is claiming a check it never ran.
 */
export function CriticalAttention() {
  const navigate = useNavigate()
  const halts = useEtlHalts(false)
  const live = useLiveOperations()
  const comb = useCombSummary()
  // The rail's admin gate holds this query already; it says which categories a halt lets through.
  const { data: catalog } = useEtlJobCatalog()

  const conditions = [haltCondition(halts, catalog), staleCondition(live), combCondition(comb)]

  return (
    <SectionCard title="Critical Attention" subtitle="Measured conditions — not signals">
      <Group gap={4} wrap="wrap" align="center" style={{ marginBottom: 10 }} data-signal-link="">
        <Text size="xs" c="dimmed">Signals are raised by the detector —</Text>
        <SectionAction onClick={() => navigate('/signals')}>open the Signal Inbox →</SectionAction>
      </Group>
      {isAllClear(conditions) ? (
        <EmptyState icon={<IconCircleCheck size={28} stroke={1.5} />} title={ALL_CLEAR_TEXT} />
      ) : (
        <div className="lh-overview-grid lh-overview-thirds">
          {conditions.map((c) => <ConditionCard key={c.key} condition={c} />)}
        </div>
      )}
    </SectionCard>
  )
}
